import * as THREE from 'three';
import { Inventory, type Stack, type ItemId, FOOD_IDS, ITEMS } from './inventory';
import { cookResult } from './consumables';
import { makeGlowTexture } from './nodes';
import { nearestOnRay } from './aim';
import { DEPLOY_HP, TORCH_FX } from '../config';
import type { Box, BoxPool } from '../engine/collision';
import { preloadGLB, makeGLBBuildable } from '../engine/gltf';

// Owner-modelled GLB (2026-07-09), same instant-placeholder-then-swap pattern as every buildable in
// craftables.ts: buildProceduralCampfireStructure (below) is the procedural fallback shown while the real
// model loads. The flame/light/glow fire EFFECT stays procedural either way (see buildCampfireMesh) -- it's
// the animated, functional part driven every frame by lit-state, independent of which logs/stones surround it.
const CAMPFIRE_FILE = 'campfire.glb';
preloadGLB('/models/' + CAMPFIRE_FILE);

// Deployable CAMPFIRE: a craftable cooking station you place anywhere (ground or inside). Interact (E) to open
// its contents -- a small grid -- and drop in WOOD (fuel) + RAW food (owner #6: one fire holds ~1 wood + 4 foods).
// While it is ON and has wood it BURNS: a warm light (~1.5x a torch), it eats a log every WOOD_BURN_SEC leaving
// CHARCOAL behind, and any raw food present cooks to its done form over time. Take the cooked food + charcoal
// back out. Its contents persist with the local world save; the burn/cook state recomputes on the next tick.

const WOOD_BURN_SEC = 6;   // seconds one log lasts (and the cadence charcoal is produced at)
const FIRE_COLOR = 0xff7a2a;
const LIGHT_MULT = 1.5;    // owner #6: a lit fire throws ~1.5x a torch's brightness (mirrors TORCH_FX below)

// What the fire will accept from the player: WOOD (fuel) + every RAW cookable food. Cooked food + charcoal are
// OUTPUTS -- the player takes them out but can't push them back in (keeps the grid reading as fuel + raw in).
export const CAMPFIRE_ACCEPT: ReadonlySet<ItemId> = new Set<ItemId>(['wood', ...FOOD_IDS.filter((id) => !!cookResult(id))]);

// The Rust-style FUEL / INPUT / OUTPUT layout the UI renders (ui/storage.ts `sections`), backed by the 4-slot
// Inventory below: [0] = fuel, [1] = input, [2]+[3] = outputs. Slots stay fixed so save/UI/cook all agree.
export const FUEL_SLOT = 0, INPUT_SLOT = 1, OUTPUT_SLOTS = [2, 3] as const;
export const CAMPFIRE_SECTIONS: ReadonlyArray<{ label: string; slots: number[] }> = [
  { label: 'Fuel', slots: [FUEL_SLOT] },
  { label: 'Input', slots: [INPUT_SLOT] },
  { label: 'Output', slots: [...OUTPUT_SLOTS] },
];

// Per-slot role gate: FUEL takes only wood, INPUT only raw cookable food, OUTPUT is take-only (the fire fills it
// with cooked food + charcoal; the player pulls those out but never pushes anything in). Drives ui/storage.ts.
export function campfireSlotAccept(slot: number, id: ItemId): boolean {
  if (slot === FUEL_SLOT) return id === 'wood';
  if (slot === INPUT_SLOT) return !!cookResult(id);
  return false;
}

export class Campfire {
  readonly group = new THREE.Group();
  readonly pos: THREE.Vector3;
  readonly contents = new Inventory(4); // typed slots: [0] fuel wood, [1] input raw food, [2]+[3] outputs (cooked + charcoal)
  readonly cols = 2;                    // fallback grid dims; the UI renders CAMPFIRE_SECTIONS instead of a raw grid
  readonly rows = 2;
  readonly title = 'Camp Fire';
  lit = false;
  hp = DEPLOY_HP.campfire ?? 100; // damageable like every placeable (owner 2026-07-12); at 0 it breaks + spills
  on = false;  // player switch (the CONTROLS "Turn On" button). A fresh fire starts OFF -- it never burns fuel or cooks until lit.
  // MP: the AUTHORITY owns burn/cook (contents live on the DO; the local `contents` stays empty), so the flame
  // follows this server-pushed flag instead of the local sim. null = single-player (local sim drives lit).
  netLit: boolean | null = null;
  // Light-range clamp while the fire sits under a roof (an enclosed hearth must not glow through the walls
  // and light the whole island -- cheap point lights have no occlusion). 1 = open air; main drives it.
  rangeScale = 1;
  toggle(): boolean { this.on = !this.on; if (!this.on && this.lit) this.setLit(false); return this.on; }
  private cookT = 0;   // seconds of cooking accumulated toward the current item
  private burnT = 0;   // seconds since the last wood was consumed
  private readonly light: THREE.PointLight;
  private readonly flame: THREE.Mesh;
  private readonly glow: THREE.Sprite;
  private flick = 0;

  constructor(pos: THREE.Vector3, yaw: number) {
    this.pos = pos.clone();
    const built = buildCampfireMesh(this.group);
    this.light = built.light;
    this.flame = built.flame;
    this.glow = built.glow;
    this.group.position.copy(this.pos);
    this.group.rotation.y = yaw;
    this.group.traverse((o) => { o.userData.campfire = this; }); // backref for aim-targeting
    this.setLit(false);
  }

  private setLit(v: boolean): void {
    this.lit = v;
    this.light.visible = v;
    this.flame.visible = v;
    this.glow.visible = v;
  }

  // Burn/cook tick over the TYPED slots. A fire burns whenever it is ON and the FUEL slot holds wood (it does not
  // need food -- it can run just for light + charcoal). While burning it eats one log per WOOD_BURN_SEC and drops
  // CHARCOAL into an OUTPUT slot, and any raw food in the INPUT slot cooks to its done form (also into an OUTPUT
  // slot). Out of fuel or switched off -> go dark. Returns true while actively burning so the manager flickers the
  // light. lightBoost (>= 1) is main's night punch-through; intensity is set absolutely each tick.
  update(dt: number, time: number, lightBoost = 1): boolean {
    if (this.netLit !== null) { // MP: render the server's lit flag; never run the local burn/cook sim
      if (this.lit !== this.netLit) this.setLit(this.netLit);
      if (this.netLit) this.animateFlame(time, lightBoost);
      return this.netLit;
    }
    if (!this.on) { if (this.lit) this.setLit(false); this.burnT = 0; return false; } // switched OFF
    const fuel = this.contents.stackAt(FUEL_SLOT);
    if (!fuel || fuel.id !== 'wood' || fuel.n <= 0) { if (this.lit) this.setLit(false); this.burnT = 0; return false; } // no fuel -> dark
    if (!this.lit) this.setLit(true);
    // Burn a log over time; each spent log leaves charcoal in an output slot (lost only if both outputs are full).
    this.burnT += dt;
    if (this.burnT >= WOOD_BURN_SEC) {
      this.burnT -= WOOD_BURN_SEC;
      if (this.contents.takeFromSlot(FUEL_SLOT, 1) > 0) this.addOutput('charcoal', 1);
    }
    // Cook the raw food in the INPUT slot; convert to an OUTPUT slot when its timer completes. Only consume the raw
    // once the cooked item actually lands in an output (else hold at 'done' and retry when the player frees space).
    const raw = this.contents.stackAt(INPUT_SLOT);
    const recipe = raw ? cookResult(raw.id) : null;
    if (raw && recipe) {
      this.cookT += dt;
      if (this.cookT >= recipe.sec) {
        if (this.addOutput(recipe.into, 1) > 0) { this.contents.takeFromSlot(INPUT_SLOT, 1); this.cookT = 0; }
        else this.cookT = recipe.sec; // outputs full: keep it done, wait for room
      }
    } else this.cookT = 0;
    this.animateFlame(time, lightBoost);
    return true;
  }

  // Flicker the fire a touch. A lit fire reads ~1.5x a torch (LIGHT_MULT * TORCH_FX.intensity), scaled by the
  // same night boost the torch gets so the ratio holds after dark. Shared by the local sim + the MP netLit path.
  private animateFlame(time: number, lightBoost: number): void {
    this.flick = 0.8 + 0.2 * Math.sin(time * 11) + 0.08 * Math.sin(time * 27);
    this.light.distance = TORCH_FX.distance * 0.5 * this.rangeScale; // clamped while roofed-in (see rangeScale)
    this.light.intensity = TORCH_FX.intensity * LIGHT_MULT * this.flick * lightBoost * (0.6 + 0.4 * this.rangeScale);
    this.flame.scale.y = 0.9 + 0.2 * this.flick;
    this.glow.scale.setScalar(1.7 + 0.4 * this.flick); // glow breathes with the flame (base matches the bigger fire)
    (this.glow.material as THREE.SpriteMaterial).opacity = 0.5 + 0.25 * this.flick;
  }

  // Place a produced item (cooked food / charcoal) into the OUTPUT slots, first-fit across them. Returns how many
  // landed (0 = both output slots are full or hold other items). Never touches the fuel/input slots.
  private addOutput(id: ItemId, n: number): number {
    let left = n;
    for (const i of OUTPUT_SLOTS) { if (left <= 0) break; left -= this.contents.addToSlot(i, id, left); }
    return n - left;
  }
}

export class CampfireManager {
  readonly group = new THREE.Group();
  private readonly list: Campfire[] = [];
  private readonly cols: Array<{ x: number; y: number; z: number; yaw: number }> = []; // parallel to list

  deploy(pos: THREE.Vector3, yaw: number): Campfire {
    const c = new Campfire(pos, yaw);
    this.list.push(c);
    this.group.add(c.group);
    this.cols.push({ x: pos.x, y: pos.y, z: pos.z, yaw });
    return c;
  }

  // Append each campfire's footprint to the world collider set (you can't walk through a fire).
  // Half-extents track the scaled-up mesh (stone ring at r=0.48) and the FOOT entry in deployables.ts.
  collectColliders(out: Box[], pool: BoxPool): void {
    for (const p of this.cols) out.push(pool.take(p.x, p.y + 0.23, p.z, 0.48, 0.23, 0.48, p.yaw));
  }

  aimed(raycaster: THREE.Raycaster): Campfire | null {
    for (const h of raycaster.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.campfire) return o.userData.campfire as Campfire; o = o.parent; }
    }
    return nearestOnRay(raycaster, this.list.map((c) => ({ x: c.pos.x, y: c.pos.y + 0.2, z: c.pos.z, rec: c })), 0.45);
  }

  nearest(p: THREE.Vector3, range: number): Campfire | null {
    let best: Campfire | null = null, bestSq = range * range;
    for (const c of this.list) {
      const dx = c.pos.x - p.x, dz = c.pos.z - p.z, sq = dx * dx + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = c; }
    }
    return best;
  }

  remove(c: Campfire): void {
    const i = this.list.indexOf(c);
    if (i < 0) return;
    this.list.splice(i, 1);
    this.cols.splice(i, 1);
    this.group.remove(c.group);
  }

  // Hammer PICK-UP (owner 2026-07-12): an EMPTY campfire returns to the inventory as its item.
  pickupDeny(c: Campfire): string | null {
    return c.contents.entries().length ? 'Must be empty' : null;
  }
  pickupAt(x: number, y: number, z: number): 'campfire' | null {
    const c = this.nearest(new THREE.Vector3(x, y, z), 0.9);
    if (!c || this.pickupDeny(c)) return null;
    this.remove(c);
    return 'campfire';
  }

  // Direct damage near a melee/blast impact (owner 2026-07-12: every placeable has HP -- campfire 100,
  // DEPLOY_HP). At 0 the fire BREAKS (no item back) and its contents (fuel/food/charcoal) spill via the
  // callback -- the same contract as ChestManager.damageAt.
  damageAt(x: number, y: number, z: number, dmg: number, drop: (items: Array<{ id: ItemId; n: number }>, x: number, z: number) => void, range = 1.4): boolean {
    const best = this.nearest(new THREE.Vector3(x, y, z), range);
    if (!best) return false;
    best.hp -= dmg;
    if (best.hp > 0) return true;
    const items: Array<{ id: ItemId; n: number }> = [];
    for (const [id, n] of best.contents.entries()) items.push({ id, n });
    const px = best.pos.x, pz = best.pos.z;
    this.remove(best);
    if (items.length) drop(items, px, pz);
    return true;
  }

  update(dt: number, time: number, lightBoost = 1): void {
    for (const c of this.list) c.update(dt, time, lightBoost);
  }

  // Clamp every fire's light range by whether it sits under a roof (main passes builder.isUnderRoof).
  // Called on a slow cadence -- the roof test walks placed pieces and must not run per frame.
  clampCovered(covered: (x: number, y: number, z: number) => boolean): void {
    for (const c of this.list) c.rangeScale = covered(c.pos.x, c.pos.y, c.pos.z) ? 0.42 : 1;
  }

  // --- LOCAL WORLD SAVE (game/persist.ts): replay deploy() per fire, restore the contents + the on/off switch. The
  // lit state and cook progress recompute from the contents on the next update tick. ---
  serialize(): SavedCampfire[] {
    return this.list.map((c) => ({ x: c.pos.x, y: c.pos.y, z: c.pos.z, yaw: c.group.rotation.y, on: c.on, contents: c.contents.serializeSlots() }));
  }
  deserialize(data: SavedCampfire[]): void {
    for (const s of data) {
      const c = this.deploy(new THREE.Vector3(s.x, s.y, s.z), s.yaw);
      c.on = !!s.on;
      // ROLE-ROUTE each saved stack into the typed slots instead of a raw index copy. This is idempotent for the
      // current 4-slot layout AND migrates OLD free-grid saves (the campfire used to be a 6/12-slot untyped grid):
      // wood -> fuel, raw food -> input, cooked food + charcoal (+ any overflow) -> outputs. Never dumps food into
      // the fuel slot (which would brick the fire) and never silently drops the slots beyond index 3 that a plain
      // deserializeSlots(4) would lose. Anything with no room left (both outputs full) is discarded -- rare.
      for (const stack of s.contents) {
        if (!stack || !(stack.id in ITEMS) || stack.n <= 0) continue;
        let left = stack.n;
        if (campfireSlotAccept(FUEL_SLOT, stack.id)) left -= c.contents.addToSlot(FUEL_SLOT, stack.id, left);
        else if (campfireSlotAccept(INPUT_SLOT, stack.id)) left -= c.contents.addToSlot(INPUT_SLOT, stack.id, left);
        for (const o of OUTPUT_SLOTS) { if (left <= 0) break; left -= c.contents.addToSlot(o, stack.id, left); }
      }
    }
  }
}

// Local world save shape for one campfire (`on` optional so pre-switch saves load as OFF).
export interface SavedCampfire { x: number; y: number; z: number; yaw: number; on?: boolean; contents: Array<Stack | null>; }

// The stones/logs/spit structure (GLB-backed, procedural fallback while it loads) + the flame/light/glow
// fire effect (always procedural -- the part driven every frame by lit-state).
export function buildCampfireMesh(g: THREE.Group): { light: THREE.PointLight; flame: THREE.Mesh; glow: THREE.Sprite } {
  g.add(makeGLBBuildable(CAMPFIRE_FILE, 1, buildProceduralCampfireStructure));
  // Flame (a glowing cone) + a warm point light, both toggled with `lit`.
  const flame = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.55, 6), new THREE.MeshBasicMaterial({ color: FIRE_COLOR }));
  flame.position.y = 0.36;
  g.add(flame);
  // Warm point light. Distance/decay mirror the torch (config TORCH_FX) so "1.5x a torch" is a fair comparison;
  // distance is bounded to ~half the torch's reach so a hearth lights its room brightly without washing the whole
  // island (and to keep overdraw down when several fires burn -- perf). Intensity is driven each tick in update().
  const light = new THREE.PointLight(FIRE_COLOR, 0, TORCH_FX.distance * 0.5, 1.0);
  light.position.y = 0.55;
  g.add(light);
  // Soft additive glow so the fire reads as a light source / bloom at night (not just a flat cone).
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeGlowTexture(), color: 0xffb060, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0.6 }));
  glow.position.y = 0.45;
  glow.scale.setScalar(1.8);
  g.add(glow);
  return { light, flame, glow };
}
// Scaled up ~15% with the other hero deployables (owner: models "feel a tad too small"), plus rustic
// dressing: a scorched ground plate, split spare logs tucked by the ring, and a cooking spit.
function buildProceduralCampfireStructure(g: THREE.Group): void {
  const stone = new THREE.MeshLambertMaterial({ color: 0x6f747a, flatShading: true });
  const wood = new THREE.MeshLambertMaterial({ color: 0x5a3b22, flatShading: true });
  const char = new THREE.MeshLambertMaterial({ color: 0x211d18, flatShading: true });
  // Scorched earth under the fire (receives shadow only; flat enough to never z-fight the terrain).
  const scorch = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.016, 12), char);
  scorch.position.y = 0.01; scorch.receiveShadow = true;
  g.add(scorch);
  // Ring of stones.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const s = new THREE.Mesh(new THREE.DodecahedronGeometry(0.15 + (i % 2) * 0.035, 0), stone);
    s.position.set(Math.cos(a) * 0.48, 0.09, Math.sin(a) * 0.48);
    s.castShadow = true; s.receiveShadow = true;
    g.add(s);
  }
  // A couple of crossed logs.
  for (let i = 0; i < 3; i++) {
    const log = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.7, 5), wood);
    log.rotation.z = Math.PI / 2;
    log.rotation.y = (i / 3) * Math.PI;
    log.position.y = 0.08;
    log.castShadow = true;
    g.add(log);
  }
  // Spare split logs stacked against the stones.
  for (let i = 0; i < 2; i++) {
    const spare = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.055, 0.42, 5), wood);
    spare.rotation.x = Math.PI / 2; spare.rotation.z = 0.35 + i * 0.5;
    spare.position.set(0.42, 0.07 + i * 0.09, -0.28);
    spare.castShadow = true;
    g.add(spare);
  }
  // Cooking spit: two forked uprights + a crossbar over the flame.
  for (const sx of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.025, 0.62, 5), wood);
    post.position.set(sx * 0.34, 0.31, 0.05); post.rotation.z = sx * 0.08;
    post.castShadow = true;
    g.add(post);
  }
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.78, 5), wood);
  bar.rotation.z = Math.PI / 2; bar.position.set(0, 0.62, 0.05); bar.castShadow = true;
  g.add(bar);
}
